import { createSignal, Show, For, onMount } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useAuth } from '../lib/auth'
import { createTopUp, createCheckIn, getWalletPass, getVenue } from '../lib/api'

type View = 'pass' | 'topup' | 'checkin' | 'confirm'

export default function Pass() {
  const navigate = useNavigate()
  const auth = useAuth()

  const [view, setView] = createSignal<View>('pass')
  const [topUpAmount, setTopUpAmount] = createSignal(500) // €5.00 in cents
  const [isProcessing, setIsProcessing] = createSignal(false)
  const [walletPass, setWalletPass] = createSignal<{
    passId: string
    addToWalletUrl: string
    balance: number
    itemsCheckedIn: number
  } | null>(null)
  const [venueSlug, setVenueSlug] = createSignal('')
  const [venue, setVenue] = createSignal<any>(null)
  const [selectedItems, setSelectedItems] = createSignal<Array<{ type: string; price: number }>>([])
  const [checkInResult, setCheckInResult] = createSignal<any>(null)

  onMount(async () => {
    if (!auth.isAuthenticated()) {
      navigate('/signup')
      return
    }
    await loadPass()
  })

  async function loadPass() {
    const res = await getWalletPass()
    if (res.data) {
      setWalletPass(res.data)
    }
  }

  async function handleTopUp() {
    setIsProcessing(true)
    const res = await createTopUp(topUpAmount())
    if (res.data) {
      // Redirect to Stripe Checkout
      // In production, use Stripe.js to confirm payment
      window.location.href = `https://checkout.stripe.com/pay/${res.data.clientSecret}`
    }
    setIsProcessing(false)
  }

  async function handleScanVenue() {
    if (!venueSlug()) return
    setIsProcessing(true)
    const res = await getVenue(venueSlug())
    if (res.data) {
      setVenue(res.data)
      setSelectedItems([{ type: res.data.itemTypes?.[0]?.name || 'Item', price: res.data.itemTypes?.[0]?.price || 99 }])
      setView('checkin')
    }
    setIsProcessing(false)
  }

  function addItem() {
    const v = venue()
    if (!v) return
    const itemType = v.itemTypes?.[0] || { name: 'Item', price: 99 }
    setSelectedItems([...selectedItems(), { type: itemType.name, price: itemType.price }])
  }

  function removeItem(index: number) {
    setSelectedItems(selectedItems().filter((_, i) => i !== index))
  }

  async function handleCheckIn() {
    setIsProcessing(true)
    const v = venue()
    if (!v) return

    const res = await createCheckIn(
      v.id,
      selectedItems().map((item) => ({ type: item.type })),
    )

    if (res.data) {
      setCheckInResult(res.data)
      setView('confirm')
      await auth.refreshProfile()
      await loadPass()
    }
    setIsProcessing(false)
  }

  const balance = () => auth.user()?.balance ?? walletPass()?.balance ?? 0
  const itemsCheckedIn = () => walletPass()?.itemsCheckedIn ?? 0
  const totalCheckIn = () => selectedItems().reduce((sum, item) => sum + item.price, 0)
  const hasEnoughBalance = () => balance() >= totalCheckIn()

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      {/* Header */}
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center justify-between">
          <div class="flex items-center gap-2">
            <img src="/logo.svg" alt="ZipCheck" class="h-6" />
            <span class="font-semibold text-gray-900">ZipCheck</span>
          </div>
          <button
            onClick={() => auth.logout()}
            class="text-sm text-gray-500 hover:text-gray-700"
          >
            Sign out
          </button>
        </div>
      </header>

      <main class="flex-1 p-4">
        <div class="max-w-md mx-auto">
          {/* Pass View */}
          <Show when={view() === 'pass'}>
            {/* Balance Card */}
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
              <p class="text-sm text-gray-500 mb-1">Your balance</p>
              <p class="text-3xl font-bold text-gray-900">
                €{(balance() / 100).toFixed(2)}
              </p>

              <Show when={balance() < 100}>
                <p class="mt-2 text-xs text-amber-600">
                  Low balance — top up to check in at venues.
                </p>
              </Show>

              <div class="mt-4 flex gap-2">
                <button
                  onClick={() => setView('topup')}
                  class="flex-1 py-2.5 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 active:bg-emerald-800 transition-colors text-sm"
                >
                  Top up
                </button>
                <button
                  onClick={() => setView('checkin')}
                  class="flex-1 py-2.5 px-4 bg-white text-gray-700 font-medium rounded-lg
                         border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                >
                  Check in
                </button>
              </div>
            </div>

            {/* Active Items */}
            <Show when={itemsCheckedIn() > 0}>
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
                <h2 class="text-sm font-semibold text-gray-700 mb-2">Active items</h2>
                <p class="text-lg font-bold text-gray-900">
                  {itemsCheckedIn()} item{itemsCheckedIn() !== 1 ? 's' : ''} checked in
                </p>
              </div>
            </Show>

            {/* Wallet Pass */}
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
              <h2 class="text-sm font-semibold text-gray-700 mb-2">Your ZipPass</h2>

              <Show
                when={walletPass()?.addToWalletUrl}
                fallback={
                  <button
                    onClick={async () => {
                      setIsProcessing(true)
                      const res = await createCheckIn
                      setIsProcessing(false)
                    }}
                    class="w-full py-3 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg
                           hover:bg-gray-200 transition-colors text-sm"
                  >
                    Get your wallet pass
                  </button>
                }
              >
                <a
                  href={walletPass()!.addToWalletUrl}
                  target="_blank"
                  rel="noopener"
                  class="block w-full py-3 px-4 bg-gray-900 text-white font-semibold rounded-lg
                         hover:bg-gray-800 transition-colors text-sm text-center"
                >
                  Add to Google Wallet
                </a>
              </Show>
            </div>

            {/* Referral */}
            <Show when={auth.user()?.referralCode}>
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h2 class="text-sm font-semibold text-gray-700 mb-1">Invite friends</h2>
                <p class="text-xs text-gray-500 mb-3">
                  You both get 10% off check-ins.
                </p>
                <div class="flex items-center gap-2">
                  <input
                    type="text"
                    readonly
                    value={auth.user()?.referralCode || ''}
                    class="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg
                           text-sm font-mono text-gray-700"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(auth.user()?.referralCode || '')}
                    class="py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium
                           hover:bg-gray-200 transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </Show>
          </Show>

          {/* Top Up View */}
          <Show when={view() === 'topup'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button
                onClick={() => setView('pass')}
                class="text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                ← Back
              </button>

              <h1 class="text-xl font-bold text-gray-900 mb-2">Top up your balance</h1>
              <p class="text-sm text-gray-600 mb-6">
                Load credits to check in instantly at any venue.
              </p>

              <div class="grid grid-cols-3 gap-3 mb-6">
                <For each={[500, 1000, 2000, 5000, 10000, 20000]}>
                  {(amount) => (
                    <button
                      onClick={() => setTopUpAmount(amount)}
                      class={`py-3 px-4 rounded-lg text-sm font-semibold transition-colors
                        ${topUpAmount() === amount
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      €{(amount / 100).toFixed(0)}
                    </button>
                  )}
                </For>
              </div>

              <div class="mb-6 p-4 bg-gray-50 rounded-lg">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Current balance</span>
                  <span class="font-medium">€{(balance() / 100).toFixed(2)}</span>
                </div>
                <div class="flex justify-between text-sm mt-1">
                  <span class="text-gray-500">Loading</span>
                  <span class="font-medium text-emerald-600">+€{(topUpAmount() / 100).toFixed(2)}</span>
                </div>
                <div class="border-t border-gray-200 mt-2 pt-2 flex justify-between text-sm">
                  <span class="font-medium text-gray-700">New balance</span>
                  <span class="font-bold">€{((balance() + topUpAmount()) / 100).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handleTopUp}
                disabled={isProcessing()}
                class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                       hover:bg-emerald-700 active:bg-emerald-800
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
              >
                {isProcessing() ? 'Processing...' : `Load €${(topUpAmount() / 100).toFixed(2)}`}
              </button>

              <p class="mt-3 text-xs text-gray-500 text-center">
                Powered by Stripe. Card, Apple Pay, Google Pay accepted.
              </p>
            </div>
          </Show>

          {/* Check In View */}
          <Show when={view() === 'checkin'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button
                onClick={() => { setView('pass'); setVenue(null); setSelectedItems([]) }}
                class="text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                ← Back
              </button>

              <Show
                when={venue()}
                fallback={
                  <>
                    <h1 class="text-xl font-bold text-gray-900 mb-2">Check in items</h1>
                    <p class="text-sm text-gray-600 mb-6">
                      Scan the venue QR code or enter the venue name.
                    </p>

                    <div class="flex gap-2">
                      <input
                        type="text"
                        placeholder="Venue name or code"
                        value={venueSlug()}
                        onInput={(e) => setVenueSlug(e.currentTarget.value)}
                        class="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-base
                               focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <button
                        onClick={handleScanVenue}
                        disabled={isProcessing() || !venueSlug()}
                        class="py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                               hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                      >
                        {isProcessing() ? '...' : 'Go'}
                      </button>
                    </div>
                  </>
                }
              >
                <h1 class="text-xl font-bold text-gray-900 mb-1">{venue()!.name}</h1>
                <p class="text-sm text-gray-500 mb-4">{venue()!.address}</p>

                <h2 class="text-sm font-semibold text-gray-700 mb-2">Items to check in</h2>

                <div class="space-y-2 mb-4">
                  <For each={selectedItems()}>
                    {(item, index) => (
                      <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p class="text-sm font-medium text-gray-900">{item.type}</p>
                          <p class="text-xs text-gray-500">€{(item.price / 100).toFixed(2)}</p>
                        </div>
                        <button
                          onClick={() => removeItem(index())}
                          class="text-sm text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </For>
                </div>

                <button
                  onClick={addItem}
                  class="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm
                         hover:bg-gray-200 transition-colors mb-4"
                >
                  + Add item
                </button>

                <div class="border-t border-gray-200 pt-4 mb-4">
                  <div class="flex justify-between text-sm">
                    <span class="text-gray-500">Total</span>
                    <span class="font-bold text-lg">€{(totalCheckIn() / 100).toFixed(2)}</span>
                  </div>
                  <div class="flex justify-between text-sm mt-1">
                    <span class="text-gray-500">Your balance</span>
                    <span class={hasEnoughBalance() ? 'text-emerald-600' : 'text-red-600'}>
                      €{(balance() / 100).toFixed(2)}
                    </span>
                  </div>
                </div>

                <Show when={!hasEnoughBalance()}>
                  <div class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    Not enough balance.{' '}
                    <button
                      onClick={() => setView('topup')}
                      class="font-semibold underline"
                    >
                      Top up now
                    </button>
                  </div>
                </Show>

                <button
                  onClick={handleCheckIn}
                  disabled={isProcessing() || !hasEnoughBalance() || selectedItems().length === 0}
                  class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 active:bg-emerald-800
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
                >
                  {isProcessing() ? 'Processing...' : `Check in ${selectedItems().length} item${selectedItems().length !== 1 ? 's' : ''}`}
                </button>
              </Show>
            </div>
          </Show>

          {/* Confirmation View */}
          <Show when={view() === 'confirm'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">✓</span>
              </div>

              <h1 class="text-xl font-bold text-gray-900 mb-2">Checked in!</h1>
              <p class="text-sm text-gray-600 mb-4">
                {checkInResult()?.items?.length || 0} item{(checkInResult()?.items?.length || 0) !== 1 ? 's' : ''} checked in at {venue()?.name}
              </p>

              <div class="p-4 bg-gray-50 rounded-lg mb-6">
                <Show when={checkInResult()?.qrPayload}>
                  <div class="mb-3">
                    <p class="text-xs text-gray-500 mb-2">Show this QR to staff</p>
                    <div class="w-48 h-48 bg-white border-2 border-gray-200 rounded-lg mx-auto flex items-center justify-center">
                      <p class="text-xs text-gray-400">QR Code</p>
                    </div>
                  </div>
                </Show>

                <div class="flex justify-between text-sm">
                  <span class="text-gray-500">Balance deducted</span>
                  <span class="font-medium">€{(checkInResult()?.totalAmount / 100).toFixed(2)}</span>
                </div>
                <div class="flex justify-between text-sm mt-1">
                  <span class="text-gray-500">Remaining balance</span>
                  <span class="font-bold">€{(checkInResult()?.balanceAfter / 100).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => { setView('pass'); setVenue(null); setSelectedItems([]); setCheckInResult(null) }}
                class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                       hover:bg-emerald-700 transition-colors"
              >
                Done
              </button>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
